import React, { useState, useEffect } from 'react';
import WorkflowProgressIndicator from '../../components/ui/WorkflowProgressIndicator';
import FileUploadZone from './components/FileUploadZone';
import FileQueueList from './components/FileQueueList';
import UploadGuidelines from './components/UploadGuidelines';
import UploadSummary from './components/UploadSummary';
import Icon from '../../components/AppIcon';

const FileUploadDashboard = () => {
  const [files, setFiles] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);

  // Mock file data for demonstration
  const mockFiles = [
    {
      id: 1,
      name: "supplier_a_electronics.csv",
      size: 2456789,
      type: "text/csv",
      status: "completed",
      progress: 100,
      uploadedAt: new Date(Date.now() - 300000)
    },
    {
      id: 2,
      name: "techworld_distributors_q4.xlsx",
      size: 5234567,
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      status: "completed",
      progress: 100,
      uploadedAt: new Date(Date.now() - 180000)
    },
    {
      id: 3,
      name: "global_parts_catalog.csv",
      size: 8901234,
      type: "text/csv",
      status: "uploading",
      progress: 67,
      uploadedAt: new Date()
    }
  ];

  useEffect(() => {
    // Simulate loading existing files
    const timer = setTimeout(() => {
      setFiles(mockFiles);
    }, 500);

    return () => clearTimeout(timer);
  }, []);

  // Simulate file upload progress
  useEffect(() => {
    const interval = setInterval(() => {
      setFiles(prevFiles => 
        prevFiles?.map(file => {
          if (file?.status === 'uploading' && file?.progress < 100) {
            const newProgress = Math.min(file?.progress + Math.random() * 15, 100);
            return {
              ...file,
              progress: newProgress,
              status: newProgress >= 100 ? 'completed' : 'uploading'
            };
          }
          return file;
        })
      );
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  const handleFilesSelected = (selectedFiles) => {
    const newFiles = selectedFiles?.map((file, index) => ({
      id: Date.now() + index,
      name: file?.name,
      size: file?.size,
      type: file?.type,
      status: 'uploading',
      progress: 0,
      uploadedAt: new Date(),
      file: file
    }));

    setFiles(prevFiles => [...prevFiles, ...newFiles]);

    // Simulate upload progress for new files
    newFiles?.forEach((newFile) => {
      const uploadInterval = setInterval(() => {
        setFiles(prevFiles => 
          prevFiles?.map(file => {
            if (file?.id === newFile?.id && file?.status === 'uploading') {
              const newProgress = Math.min(file?.progress + Math.random() * 20, 100);
              const newStatus = newProgress >= 100 ? 'completed' : 'uploading';
              
              if (newStatus === 'completed') {
                clearInterval(uploadInterval);
              }
              
              return {
                ...file,
                progress: newProgress,
                status: newStatus
              };
            }
            return file;
          })
        );
      }, 500);
    });
  };

  const handleRemoveFile = (fileId) => {
    setFiles(prevFiles => prevFiles?.filter(file => file?.id !== fileId));
  };

  const handleClearAll = () => {
    setFiles([]);
  };

  const handleRetryFile = (fileId) => {
    setFiles(prevFiles => 
      prevFiles?.map(file => 
        file?.id === fileId 
          ? { ...file, status: 'uploading', progress: 0, error: null }
          : file
      )
    );

    // Simulate retry upload
    const retryInterval = setInterval(() => {
      setFiles(prevFiles => 
        prevFiles?.map(file => {
          if (file?.id === fileId && file?.status === 'uploading') {
            const newProgress = Math.min(file?.progress + Math.random() * 25, 100);
            const shouldFail = Math.random() < 0.1; // 10% chance to fail again
            
            if (shouldFail && newProgress > 50) {
              clearInterval(retryInterval);
              return {
                ...file,
                status: 'error',
                error: 'Network connection lost during upload'
              };
            }
            
            if (newProgress >= 100) {
              clearInterval(retryInterval);
              return {
                ...file,
                progress: 100,
                status: 'completed'
              };
            }
            
            return {
              ...file,
              progress: newProgress
            };
          }
          return file;
        })
      );
    }, 400);
  };

  const handleProcessFiles = () => {
    setIsProcessing(true);
    
    // Simulate processing delay
    setTimeout(() => {
      setIsProcessing(false);
      // Navigate to processing status monitor
      window.location.href = '/processing-status-monitor';
    }, 2000);
  };

  return (
    <div className="min-h-screen bg-background">
      <main className="pt-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Page Header */}
          <div className="mb-8">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
                <Icon name="Upload" size={24} color="white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-foreground">File Upload Dashboard</h1>
                <p className="text-muted-foreground">
                  Upload supplier price lists to begin product matching analysis
                </p>
              </div>
            </div>
            
            {/* Workflow Progress */}
            <WorkflowProgressIndicator 
              currentStep={0}
              completedSteps={[]}
              className="mt-6"
            />
          </div>

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left Column - Upload Zone and Queue */}
            <div className="lg:col-span-2 space-y-6">
              {/* Upload Zone */}
              <div className="bg-card border border-border rounded-lg p-6">
                <FileUploadZone 
                  onFilesSelected={handleFilesSelected}
                  isProcessing={isProcessing}
                />
              </div>

              {/* File Queue */}
              <div className="bg-card border border-border rounded-lg p-6">
                <FileQueueList
                  files={files}
                  onRemoveFile={handleRemoveFile}
                  onClearAll={handleClearAll}
                  onRetryFile={handleRetryFile}
                />
              </div>

              {/* Upload Summary */}
              <UploadSummary
                files={files}
                onProcessFiles={handleProcessFiles}
                isProcessing={isProcessing}
              />
            </div>

            {/* Right Column - Guidelines */}
            <div className="lg:col-span-1">
              <UploadGuidelines />
            </div>
          </div>

          {/* Quick Stats */}
          <div className="mt-8 grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-card border border-border rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-primary mb-1">
                {files?.filter(f => f?.status === 'completed')?.length}
              </div>
              <div className="text-sm text-muted-foreground">Files Ready</div>
            </div>
            
            <div className="bg-card border border-border rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-warning mb-1">
                {files?.filter(f => f?.status === 'uploading' || f?.status === 'processing')?.length}
              </div>
              <div className="text-sm text-muted-foreground">Processing</div>
            </div>
            
            <div className="bg-card border border-border rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-error mb-1">
                {files?.filter(f => f?.status === 'error')?.length}
              </div>
              <div className="text-sm text-muted-foreground">Failed</div>
            </div>
            
            <div className="bg-card border border-border rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-foreground mb-1">
                {files?.length}
              </div>
              <div className="text-sm text-muted-foreground">Total Files</div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default FileUploadDashboard;